import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { analyticsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  TrendingUp,
  Sparkles,
  Target,
  Clock,
  Hash,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Zap,
  Eye,
  Heart,
  MessageCircle,
  Share2,
  Upload,
  Video,
  FileVideo,
  Lightbulb,
  Award,
  BarChart3,
  Film,
} from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const getScoreColor = (score) => {
  if (score >= 80) return "text-green-500";
  if (score >= 60) return "text-yellow-500";
  if (score >= 40) return "text-orange-500";
  return "text-red-500";
};

const getScoreGradient = (score) => {
  if (score >= 80) return "from-green-500/20 to-green-500/5";
  if (score >= 60) return "from-yellow-500/20 to-yellow-500/5";
  if (score >= 40) return "from-orange-500/20 to-orange-500/5";
  return "from-red-500/20 to-red-500/5";
};

const getScoreLabel = (score) => {
  if (score >= 85) return "Viral Potential!";
  if (score >= 70) return "High Potential";
  if (score >= 55) return "Good Potential";
  if (score >= 40) return "Needs Work";
  return "Needs Improvement";
};

export default function ViralPredictorPage() {
  const [activeTab, setActiveTab] = useState("script");
  
  // Script analysis state
  const [script, setScript] = useState("");
  const [title, setTitle] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [hashtagInput, setHashtagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Video analysis state
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoResult, setVideoResult] = useState(null);

  // Script analysis handlers
  const handleAddHashtag = (e) => {
    e.preventDefault();
    if (hashtagInput && !hashtags.includes(hashtagInput)) {
      const tag = hashtagInput.startsWith("#") ? hashtagInput : `#${hashtagInput}`;
      setHashtags([...hashtags, tag]);
      setHashtagInput("");
    }
  };

  const removeHashtag = (tag) => {
    setHashtags(hashtags.filter((t) => t !== tag));
  };

  const handleAnalyze = async () => {
    if (!script.trim()) {
      toast.error("Please enter your script");
      return;
    }

    setLoading(true);
    try {
      const response = await analyticsAPI.getViralScore({
        script,
        title,
        hashtags,
      });
      setResult(response.data);
      toast.success("Analysis complete!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to analyze script");
    } finally {
      setLoading(false);
    }
  };

  // Video analysis handlers
  const handleVideoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.includes("video")) {
      toast.error("Please select a video file");
      return;
    }

    // Validate file size (100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video must be under 100MB");
      return;
    }

    setVideoFile(file);
    setVideoResult(null);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setVideoPreview(url);
  };

  const handleVideoAnalyze = async () => {
    if (!videoFile) {
      toast.error("Please select a video first");
      return;
    }

    setVideoLoading(true);
    setUploadProgress(0);

    try {
      const response = await analyticsAPI.analyzeVideo(videoFile, (progressEvent) => {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(progress);
      });
      
      setVideoResult(response.data);
      toast.success("Video analysis complete!");
    } catch (error) {
      console.error("Video analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze video. Please try again.");
    } finally {
      setVideoLoading(false);
      setUploadProgress(0);
    }
  };

  const clearVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setVideoResult(null);
    setUploadProgress(0);
  };

  // Shared result rendering
  const renderScoreCircle = (score, label, size = "large") => {
    const isLarge = size === "large";
    return (
      <div className={`flex flex-col items-center ${isLarge ? 'gap-2' : 'gap-1'}`}>
        <div className={`relative ${isLarge ? 'w-32 h-32' : 'w-20 h-20'}`}>
          <svg className="w-full h-full transform -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r={isLarge ? "60" : "36"}
              stroke="currentColor"
              strokeWidth={isLarge ? "8" : "6"}
              fill="none"
              className="text-secondary"
            />
            <circle
              cx="50%"
              cy="50%"
              r={isLarge ? "60" : "36"}
              stroke="currentColor"
              strokeWidth={isLarge ? "8" : "6"}
              fill="none"
              strokeDasharray={isLarge ? "377" : "226"}
              strokeDashoffset={isLarge ? 377 - (377 * score) / 100 : 226 - (226 * score) / 100}
              className={getScoreColor(score)}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`${isLarge ? 'text-3xl' : 'text-xl'} font-bold text-white`}>{score}</span>
          </div>
        </div>
        <span className={`${isLarge ? 'text-sm' : 'text-xs'} text-muted-foreground text-center`}>{label}</span>
      </div>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="max-w-6xl mx-auto space-y-8"
      data-testid="viral-predictor-page"
    >
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <TrendingUp className="w-8 h-8 text-[#FF0055]" />
          Viral Predictor
        </h1>
        <p className="text-muted-foreground mt-2">
          AI-powered analysis to predict your content's viral potential
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="script" className="gap-2" data-testid="script-tab">
            <Sparkles className="w-4 h-4" />
            Script Analysis
          </TabsTrigger>
          <TabsTrigger value="video" className="gap-2" data-testid="video-tab">
            <Video className="w-4 h-4" />
            Video Analysis
          </TabsTrigger>
        </TabsList>

        {/* Script Analysis Tab */}
        <TabsContent value="script">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <motion.div variants={itemVariants} className="space-y-6">
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-[#FF0055]" />
                    Script Details
                  </CardTitle>
                  <CardDescription>
                    Paste your script and let AI predict its viral potential
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Video Title (optional)</Label>
                    <Input
                      placeholder="e.g., 'The Secret Nobody Tells You About...'"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="bg-secondary/50 border-border/50"
                      data-testid="script-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Your Script</Label>
                    <Textarea
                      placeholder="Paste your video script here..."
                      value={script}
                      onChange={(e) => setScript(e.target.value)}
                      className="min-h-[200px] bg-secondary/50 border-border/50"
                      data-testid="script-content-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Hashtags</Label>
                    <form onSubmit={handleAddHashtag} className="flex gap-2">
                      <Input
                        placeholder="Add hashtag..."
                        value={hashtagInput}
                        onChange={(e) => setHashtagInput(e.target.value)}
                        className="bg-secondary/50 border-border/50"
                        data-testid="hashtag-input"
                      />
                      <Button type="submit" variant="outline" size="icon">
                        <Hash className="w-4 h-4" />
                      </Button>
                    </form>
                    {hashtags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {hashtags.map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="cursor-pointer hover:bg-destructive/20"
                            onClick={() => removeHashtag(tag)}
                          >
                            {tag}
                            <X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={handleAnalyze}
                    disabled={loading || !script.trim()}
                    className="w-full bg-gradient-to-r from-[#FF0055] to-[#FF0055]/80 hover:opacity-90 text-white"
                    data-testid="analyze-script-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Target className="w-4 h-4 mr-2" />
                        Analyze Script
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Script Results Section */}
            <motion.div variants={itemVariants}>
              <AnimatePresence mode="wait">
                {result ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="space-y-6"
                  >
                    {/* Overall Score */}
                    <Card className={`bg-gradient-to-br ${getScoreGradient(result.overall_score)} border-border/50`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-white">Viral Score</h3>
                            <p className={`text-2xl font-bold ${getScoreColor(result.overall_score)}`}>
                              {getScoreLabel(result.overall_score)}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {result.predicted_reach}
                            </p>
                          </div>
                          {renderScoreCircle(result.overall_score, "Overall")}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Breakdown */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">Score Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4">
                          {renderScoreCircle(result.hook_strength, "Hook", "small")}
                          {renderScoreCircle(result.engagement_potential, "Engagement", "small")}
                          {renderScoreCircle(result.trend_alignment, "Trend Fit", "small")}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Suggestions */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-yellow-500" />
                          Suggestions
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {result.suggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <CheckCircle className="w-4 h-4 text-[#00F0FF] mt-0.5 flex-shrink-0" />
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Best Times */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#00F0FF]" />
                          Best Posting Times
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {result.best_posting_times.map((time, i) => (
                            <Badge key={i} variant="outline" className="border-[#00F0FF]/30">
                              {time}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ) : (
                  <Card className="bg-card border-border/50 h-full min-h-[400px] flex items-center justify-center">
                    <div className="text-center p-8">
                      <TrendingUp className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">
                        Enter your script and click "Analyze" to get AI-powered viral predictions
                      </p>
                    </div>
                  </Card>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </TabsContent>

        {/* Video Analysis Tab */}
        <TabsContent value="video">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Video Upload Section */}
            <motion.div variants={itemVariants} className="space-y-6">
              <Card className="bg-card border-border/50">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                    <FileVideo className="w-5 h-5 text-[#00F0FF]" />
                    Upload Video
                  </CardTitle>
                  <CardDescription>
                    Upload your video for AI-powered viral potential analysis
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!videoFile ? (
                    <label 
                      className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-border/50 rounded-xl cursor-pointer hover:border-[#00F0FF]/50 transition-colors bg-secondary/20"
                      data-testid="video-upload-area"
                    >
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                        <p className="mb-2 text-sm text-muted-foreground">
                          <span className="font-semibold text-white">Click to upload</span> or drag and drop
                        </p>
                        <p className="text-xs text-muted-foreground">
                          MP4, MOV, WEBM or AVI (max 100MB)
                        </p>
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept="video/*"
                        onChange={handleVideoSelect}
                        data-testid="video-file-input"
                      />
                    </label>
                  ) : (
                    <div className="space-y-4">
                      {/* Video Preview */}
                      <div className="relative rounded-xl overflow-hidden bg-black aspect-[9/16] max-h-[400px]">
                        <video
                          src={videoPreview}
                          controls
                          className="w-full h-full object-contain"
                          data-testid="video-preview"
                        />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute top-2 right-2"
                          onClick={clearVideo}
                          data-testid="clear-video-btn"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* File Info */}
                      <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg">
                        <Film className="w-8 h-8 text-[#00F0FF]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{videoFile.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {(videoFile.size / (1024 * 1024)).toFixed(2)} MB
                          </p>
                        </div>
                      </div>

                      {/* Upload Progress */}
                      {videoLoading && uploadProgress > 0 && (
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Uploading & Analyzing...</span>
                            <span className="text-white">{uploadProgress}%</span>
                          </div>
                          <Progress value={uploadProgress} className="h-2" />
                        </div>
                      )}
                    </div>
                  )}

                  <Button
                    onClick={handleVideoAnalyze}
                    disabled={videoLoading || !videoFile}
                    className="w-full bg-gradient-to-r from-[#00F0FF] to-[#00F0FF]/80 hover:opacity-90 text-[#0A0A0B] font-bold"
                    data-testid="analyze-video-btn"
                  >
                    {videoLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {uploadProgress < 100 ? "Uploading..." : "Analyzing with AI..."}
                      </>
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Analyze Video
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Analysis typically takes 30-60 seconds depending on video length
                  </p>
                </CardContent>
              </Card>

              {/* Tips Card */}
              <Card className="bg-gradient-to-r from-[#FF0055]/10 to-[#00F0FF]/10 border-border/50">
                <CardContent className="p-4">
                  <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    Tips for Better Analysis
                  </h4>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• Upload the final edited version of your video</li>
                    <li>• Ensure good lighting and clear visuals</li>
                    <li>• Videos under 60 seconds work best for short-form platforms</li>
                    <li>• Include the actual audio/music you plan to use</li>
                  </ul>
                </CardContent>
              </Card>
            </motion.div>

            {/* Video Results Section */}
            <motion.div variants={itemVariants}>
              <AnimatePresence mode="wait">
                {videoResult ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="space-y-6"
                  >
                    {/* Overall Score */}
                    <Card className={`bg-gradient-to-br ${getScoreGradient(videoResult.overall_score)} border-border/50`}>
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-white">Viral Score</h3>
                            <p className={`text-2xl font-bold ${getScoreColor(videoResult.overall_score)}`}>
                              {getScoreLabel(videoResult.overall_score)}
                            </p>
                            <Badge variant="outline" className="mt-2 border-[#00F0FF]/30">
                              {videoResult.content_category}
                            </Badge>
                            <p className="text-sm text-muted-foreground mt-2">
                              {videoResult.predicted_reach}
                            </p>
                          </div>
                          {renderScoreCircle(videoResult.overall_score, "Overall")}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Analysis Summary */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-[#00F0FF]" />
                          Analysis Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {videoResult.analysis_summary}
                        </p>
                      </CardContent>
                    </Card>

                    {/* Detailed Breakdown */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">Detailed Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          {renderScoreCircle(videoResult.visual_appeal, "Visual", "small")}
                          {renderScoreCircle(videoResult.hook_strength, "Hook", "small")}
                          {renderScoreCircle(videoResult.engagement_potential, "Engagement", "small")}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          {renderScoreCircle(videoResult.trend_alignment, "Trend Fit", "small")}
                          {renderScoreCircle(videoResult.pacing_score, "Pacing", "small")}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Strengths */}
                    {videoResult.strengths && videoResult.strengths.length > 0 && (
                      <Card className="bg-card border-border/50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-white flex items-center gap-2">
                            <Award className="w-4 h-4 text-green-500" />
                            Strengths
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2">
                            {videoResult.strengths.map((strength, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                {strength}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* Suggestions */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-yellow-500" />
                          Suggestions for Improvement
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {videoResult.suggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <AlertCircle className="w-4 h-4 text-[#FF0055] mt-0.5 flex-shrink-0" />
                              {suggestion}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>

                    {/* Best Times */}
                    <Card className="bg-card border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-white flex items-center gap-2">
                          <Clock className="w-4 h-4 text-[#00F0FF]" />
                          Best Posting Times
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {videoResult.best_posting_times.map((time, i) => (
                            <Badge key={i} variant="outline" className="border-[#00F0FF]/30">
                              {time}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Analyze Another */}
                    <Button
                      variant="outline"
                      className="w-full border-[#00F0FF]/30"
                      onClick={clearVideo}
                      data-testid="analyze-another-btn"
                    >
                      Analyze Another Video
                    </Button>
                  </motion.div>
                ) : (
                  <Card className="bg-card border-border/50 h-full min-h-[500px] flex items-center justify-center">
                    <div className="text-center p-8">
                      <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-2">
                        Upload a video to get AI-powered viral analysis
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Get detailed scores for visual appeal, hook strength, engagement potential, and more
                      </p>
                    </div>
                  </Card>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
